import { useCallback, useEffect, useRef, useState } from "react"
import { supabase } from "@/lib/data"

export interface FormFieldMeta {
  source?: "agent" | "human" | "import"
  confidence?: number | null
  locked?: boolean
  updated_at?: string
}

export interface FormRecord {
  id: string
  form: string
  contact: string
  team: string | null
  agent: number | null
  values: Record<string, any>
  field_meta: Record<string, FormFieldMeta>
  version: number
  updated_at: string
}

export interface FormDef {
  id: string
  title: string
  fields: FormFieldDef[]
}

export interface FormWithRecord {
  form: FormDef
  record: FormRecord | null
}

export interface FormRecordEvent {
  id: string
  record: string
  field: string
  old_value: any
  new_value: any
  source: "agent" | "human" | "import"
  actor: string | null
  confidence: number | null
  note: string | null
  message: string | null
  created_at: string
}

export function useFormRecords(
  contactId: string | null | undefined,
  teamId?: string | null,
  // when > 0, silently re-fetches on this interval (ms)
  pollMs?: number,
  // when set, scope to a single agent's bound forms; when omitted, across the team's agents
  agentId?: number | string | null
) {
  const [forms, setForms] = useState<FormWithRecord[]>([])
  const [loading, setLoading] = useState(false)

  // Monotonic token so a slow, stale request (e.g. the null-agent fetch fired
  // right before the real agent is known) can't overwrite a newer response.
  const loadSeqRef = useRef(0)
  // Last committed forms serialized — skip setForms when nothing changed so the
  // 10s poll doesn't churn a new array identity and re-render/flicker the panel.
  const lastSerializedRef = useRef<string>("")

  const load = useCallback(async (silent = false) => {
    const seq = ++loadSeqRef.current
    const isStale = () => seq !== loadSeqRef.current
    const commit = (next: FormWithRecord[]) => {
      const serialized = JSON.stringify(next)
      if (serialized === lastSerializedRef.current) return
      lastSerializedRef.current = serialized
      setForms(next)
    }

    if (!contactId) {
      commit([])
      return
    }
    if (!silent) setLoading(true)
    try {
      const formIds = new Set<string>()

      if (agentId != null && agentId !== "") {
        const { data: agent } = await supabase
          .from("agents")
          .select("config")
          .eq("id", Number(agentId))
          .maybeSingle()
        if (isStale()) return
        const bound = (agent?.config as any)?.forms as string[] | undefined
        for (const f of bound || []) formIds.add(f)
      } else {
        let agentQuery = supabase.from("agents").select("id, config")
        if (teamId) agentQuery = agentQuery.eq("team_id", teamId)
        const { data: agents } = await agentQuery
        if (isStale()) return
        for (const a of agents || []) {
          const bound = (a.config as any)?.forms as string[] | undefined
          for (const f of bound || []) formIds.add(f)
        }
      }

      const { data: recordRows } = await supabase
        .from("form_records")
        .select("*")
        .eq("contact", contactId)
      if (isStale()) return
      if (agentId == null || agentId === "") {
        for (const r of recordRows || []) formIds.add(r.form)
      }

      if (formIds.size === 0) {
        commit([])
        return
      }

      const { data: formRows } = await supabase
        .from("forms")
        .select("id, title, fields")
        .in("id", Array.from(formIds))
      if (isStale()) return

      const recordByForm = new Map<string, FormRecord>()
      for (const r of recordRows || []) recordByForm.set(r.form, r as unknown as FormRecord)

      const result: FormWithRecord[] = (formRows || []).map((f) => ({
        form: { id: f.id, title: f.title || "(untitled)", fields: (f.fields as unknown as FormFieldDef[]) || [] },
        record: recordByForm.get(f.id) || null,
      }))
      commit(result)
    } finally {
      if (!silent && !isStale()) setLoading(false)
    }
  }, [contactId, teamId, agentId])

  // On scope change, reset the dedupe baseline so the new scope's first result
  // always commits (its forms may coincidentally serialize the same as before).
  useEffect(() => {
    lastSerializedRef.current = ""
  }, [contactId, teamId, agentId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!pollMs || pollMs <= 0 || !contactId) return
    const interval = setInterval(() => load(true), pollMs)
    return () => clearInterval(interval)
  }, [pollMs, contactId, load])

  const editField = useCallback(
    async (formId: string, field: string, value: any, opts?: { actor?: string; note?: string; lock?: boolean }) => {
      if (!contactId) return
      const entry = forms.find((f) => f.form.id === formId)
      const existing = entry?.record || null
      const prev = existing?.values?.[field] ?? null

      const nextValues = { ...(existing?.values || {}), [field]: value }
      const nextMeta = {
        ...(existing?.field_meta || {}),
        [field]: {
          source: "human" as const,
          confidence: null,
          locked: opts?.lock ?? true,
          updated_at: new Date().toISOString(),
        },
      }
      const nextVersion = (existing?.version ?? 0) + 1

      let recordId = existing?.id
      if (recordId) {
        await supabase
          .from("form_records")
          .update({ values: nextValues, field_meta: nextMeta as any, version: nextVersion })
          .eq("id", recordId)
      } else {
        const { data: created } = await supabase
          .from("form_records")
          .insert({
            form: formId,
            contact: contactId,
            team: teamId ?? null,
            values: nextValues,
            field_meta: nextMeta as any,
            version: nextVersion,
          })
          .select("id")
          .single()
        recordId = created?.id
      }

      if (recordId) {
        await supabase.from("form_record_events").insert({
          record: recordId,
          team: teamId ?? null,
          field,
          old_value: (prev ?? null) as any,
          new_value: (value ?? null) as any,
          source: "human",
          actor: opts?.actor ?? null,
          note: opts?.note ?? null,
        })
      }

      await load()
    },
    [contactId, teamId, forms, load]
  )

  return { forms, loading, refresh: load, editField }
}

export async function loadFieldHistory(recordId: string, field: string): Promise<FormRecordEvent[]> {
  const { data } = await supabase
    .from("form_record_events")
    .select("*")
    .eq("record", recordId)
    .eq("field", field)
    .order("created_at", { ascending: false })
  return (data || []) as unknown as FormRecordEvent[]
}
